import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaUsers, FaRocket, FaHandshake } from 'react-icons/fa';
import { Section, HorizontalLine, IconWrapper } from '../common/Sections';
import { media } from '../../utils/media';

/* Centralized Keyframe Animations */
const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const VisionSection = () => {
  return (
    <Section id="vision">
      <Container>
        <ContentWrapper>
          {/* Text Content */}
          <TextContent>
            <TitleWrapper>
              <IconWrapper>
                <FaUsers />
              </IconWrapper>
              <Title>Our Vision</Title>
              <HorizontalLine />
            </TitleWrapper>
            <Description>
              We envision a world where every athlete has access to their sports footage,
              allowing them to relive their best moments, share their highlights, and improve their game.
            </Description>
          </TextContent>

          {/* Video Content */}
          <VideoContent>
            <VideoWrapper>
              <iframe
                src="https://www.youtube.com/embed/RXuJuz5fkow"
                title="Akture Sports Vision Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </VideoWrapper>
          </VideoContent>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default VisionSection;
/* ────────────── STYLED COMPONENTS ────────────── */

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  max-width: 1200px;
  position: relative;
  z-index: 2;

  ${media.tablet(`
    padding: 0 ${({ theme }) => theme.spacing.lg};
  `)}
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr; /* Mobile-first: single column */
  gap: ${({ theme }) => theme.spacing.xl};

  ${media.desktop(`
    grid-template-columns: 1fr 1fr; /* Desktop: two columns */
    align-items: center;
  `)}
`;

const TextContent = styled.div`
  animation: ${fadeInLeft} 1s ease-out;
  text-align: center;

  ${media.desktop(`
    text-align: left;
  `)}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  ${media.desktop(`
    align-items: flex-start;
  `)}
`;

const Title = styled.h2`
  font-size: clamp(1.4rem, 3.5vw, 2rem);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.title};
  margin: 0;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 50px;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.colors.secondary}
    );
    border-radius: 2px;
  }
`;

const Description = styled.p`
  font-size: clamp(0.9rem, 2vw, 1rem);
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.55;
`;

const FeatureList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FeatureIcon = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.2rem;
  background: rgba(255, 111, 60, 0.1);
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  font-weight: 500;
`;

const VideoContent = styled.div`
  animation: ${fadeInRight} 1s ease-out;
  display: flex;
  justify-content: center;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1024px; /* Limit video width on desktop */
  height: 0;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

